/** FASE 4 · Jun 2026 — Cuentas por cobrar de SOLO LECTURA (solo Admin). */
import { useEffect, useState } from "react";
import axios from "axios";
import { useI18n } from "@/context/I18nContext";

const API = process.env.REACT_APP_BACKEND_URL;

const money = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(v || 0);

const BUCKETS = [
  { key: "not_due", label: "fin_ar_not_due", tone: "text-[#0F7B3E]" },
  { key: "due_today", label: "fin_ar_due_today", tone: "text-[#B8860B]" },
  { key: "overdue_1_7", label: "fin_ar_b_1_7", tone: "text-[#C5221F]" },
  { key: "overdue_8_30", label: "fin_ar_b_8_30", tone: "text-[#C5221F]" },
  { key: "overdue_30_plus", label: "fin_ar_b_30", tone: "text-[#C5221F]" },
  { key: "no_due_date", label: "fin_ar_no_date", tone: "text-[var(--text-secondary)]" },
];

const COLS = ["fin_ar_col_number", "fin_ar_col_customer", "fin_ar_col_phone", "fin_ar_col_invoice_date",
  "fin_ar_col_delivery", "fin_ar_col_total", "fin_ar_col_paid", "fin_ar_col_balance", "fin_ar_col_status", "fin_ar_col_aging"];

function agingLabel(r, t) {
  if (r.bucket === "no_due_date") return t("fin_ar_no_date");
  if (r.bucket === "not_due") return t("fin_ar_not_due");
  if (r.bucket === "due_today") return t("fin_ar_due_today");
  return `${r.days_overdue} ${t("fin_ar_days")}`;
}

export const ReceivablesTable = () => {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/finance/receivables`)
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, []);

  if (error) return null;
  if (!data)
    return <div data-testid="fin-ar-loading" className="text-sm font-mono-tech py-4">{t("fin_ar_loading")}</div>;

  return (
    <div data-testid="fin-ar" className="space-y-4">
      <div className="label-overline">{t("fin_ar_title")}</div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <div data-testid="fin-ar-total" className="border border-black bg-white p-3">
          <div className="text-[10px] font-mono-tech uppercase text-[var(--text-secondary)]">{t("fin_ar_total")}</div>
          <div className="font-display text-lg font-black mt-1">{money(data.total_receivables)}</div>
          <div className="text-[10px] font-mono-tech text-[var(--text-secondary)]">{data.invoice_count} inv.</div>
        </div>
        {BUCKETS.map((b) => (
          <div key={b.key} data-testid={`fin-ar-bucket-${b.key}`} className="border border-[var(--border)] bg-white p-3">
            <div className="text-[10px] font-mono-tech uppercase text-[var(--text-secondary)]">{t(b.label)}</div>
            <div className={`font-display text-lg font-black mt-1 ${b.tone}`}>{money(data.buckets[b.key]?.amount)}</div>
            <div className="text-[10px] font-mono-tech text-[var(--text-secondary)]">{data.buckets[b.key]?.count || 0} inv.</div>
          </div>
        ))}
      </div>

      {data.invoices.length === 0 ? (
        <div data-testid="fin-ar-empty" className="text-sm italic text-[var(--text-secondary)]">{t("fin_ar_empty")}</div>
      ) : (
        <div className="border border-[var(--border)] bg-white overflow-x-auto">
          <table data-testid="fin-ar-table" className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-black text-left">
                {COLS.map((c) => (
                  <th key={c} className="px-3 py-2 text-[10px] font-mono-tech uppercase tracking-wider text-[var(--text-secondary)]">{t(c)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.invoices.map((r) => (
                <tr key={r.number} data-testid={`fin-ar-row-${r.number}`} className="border-b border-[var(--border)] hover:bg-[var(--surface-alt)]">
                  <td className="px-3 py-2 font-mono-tech font-bold">{r.number}</td>
                  <td className="px-3 py-2">{r.customer || "—"}</td>
                  <td className="px-3 py-2 font-mono-tech text-xs">{r.phone || "—"}</td>
                  <td className="px-3 py-2 font-mono-tech text-xs">{r.invoice_date || "—"}</td>
                  <td className="px-3 py-2 font-mono-tech text-xs">{r.delivery_date || "—"}</td>
                  <td className="px-3 py-2 font-mono-tech text-right">{money(r.total_amount)}</td>
                  <td className="px-3 py-2 font-mono-tech text-right">{money(r.paid_total)}</td>
                  <td className="px-3 py-2 font-mono-tech text-right font-bold">{money(r.balance_due)}</td>
                  <td className="px-3 py-2 text-xs">{t(`fin_ar_st_${r.status}`)}</td>
                  <td className={`px-3 py-2 text-xs font-mono-tech ${r.days_overdue > 0 ? "text-[#C5221F] font-bold" : ""}`}>
                    {agingLabel(r, t)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
